if (!customElements.get('km-faq-tabs')) {
  class KMFaqTabs extends HTMLElement {
    constructor() {
      super();

      this.resizeFrame = null;
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.indicator = this.querySelector('.km-faq__indicator');
      this.navigationScroll = this.querySelector('.km-faq__navigation-scroll');

      if (
        !this.tablist ||
        !this.tabs.length ||
        !this.panels.length ||
        !this.indicator
      ) {
        return;
      }

      this.dataset.initialized = 'true';

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          this.activateTab(tab, {
            moveFocus: false,
            scrollIntoView: true
          });
        });

        tab.addEventListener('keydown', (event) => {
          this.handleKeydown(event, index);
        });
      });

      const selectedTab =
        this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ||
        this.tabs[0];

      this.activateTab(selectedTab, {
        moveFocus: false,
        scrollIntoView: false,
        initial: true
      });

      window.addEventListener('resize', this.handleResize);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected) this.updateIndicator();
        });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);

      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }
    }

    handleKeydown(event, currentIndex) {
      const key = event.key;

      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) {
        return;
      }

      event.preventDefault();

      let nextIndex = currentIndex;

      if (key === 'Home') {
        nextIndex = 0;
      } else if (key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else if (key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % this.tabs.length;
      } else if (key === 'ArrowLeft') {
        nextIndex =
          (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      }

      this.activateTab(this.tabs[nextIndex], {
        moveFocus: true,
        scrollIntoView: true
      });
    }

    activateTab(
      tab,
      {
        moveFocus = false,
        scrollIntoView = false,
        initial = false
      } = {}
    ) {
      if (!tab) return;

      const panelId = tab.getAttribute('aria-controls');
      const targetPanel = this.panels.find((panel) => panel.id === panelId);

      if (!targetPanel) return;

      const currentTab = this.tabs.find(
        (item) => item.getAttribute('aria-selected') === 'true'
      );

      const isSameTab = currentTab === tab && !initial;

      if (!isSameTab) {
        /*
         * 分類切換時收合所有已開啟的 FAQ，
         * 讓新分類從乾淨狀態開始。
         */
        this.querySelectorAll('.km-faq__item[open]').forEach((item) => {
          item.removeAttribute('open');
        });

        this.tabs.forEach((item) => {
          const isActive = item === tab;

          item.setAttribute('aria-selected', String(isActive));
          item.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        this.panels.forEach((panel) => {
          panel.classList.remove('is-active');

          if (panel !== targetPanel) {
            panel.hidden = true;
          }
        });

        targetPanel.hidden = false;

        if (initial) {
          targetPanel.classList.add('is-active');
        } else {
          window.requestAnimationFrame(() => {
            if (targetPanel.hidden) return;

            targetPanel.classList.add('is-active');
          });
        }
      }

      this.updateIndicator(tab);

      if (moveFocus) {
        tab.focus();
      }

      if (scrollIntoView) {
        this.keepTabVisible(tab);
      }
    }

    updateIndicator(activeTab) {
      const tab =
        activeTab ||
        this.tabs.find(
          (item) => item.getAttribute('aria-selected') === 'true'
        );

      if (!tab || !this.indicator) return;

      this.indicator.style.width = `${tab.offsetWidth}px`;
      this.indicator.style.transform = `translateX(${tab.offsetLeft}px)`;
    }

    keepTabVisible(tab) {
      if (!this.navigationScroll || !tab) return;

      const hasHorizontalOverflow =
        this.navigationScroll.scrollWidth >
        this.navigationScroll.clientWidth + 1;

      if (!hasHorizontalOverflow) return;

      const reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      tab.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }

    handleResize() {
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.updateIndicator();
        this.resizeFrame = null;
      });
    }
  }

  customElements.define('km-faq-tabs', KMFaqTabs);
}